/**
 * File validation — MIME type, extension, file size, image
 * dimension checks, and filename sanitization.
 *
 * All validation is synchronous and returns a structured
 * result so callers can surface specific error messages.
 */

import type { StorageFileCategory, ValidationResult } from './types';
import {
  FileValidationError,
  FileTooLargeError,
  InvalidMimeTypeError,
  InvalidExtensionError,
} from './errors';

const DEFAULT_MAX_SIZE_MB = 10;

const CATEGORY_CONFIG: Record<
  StorageFileCategory,
  { mimeTypes: string[]; extensions: string[]; maxSizeMB: number }
> = {
  avatar: {
    mimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
    extensions: ['jpg', 'jpeg', 'png', 'webp', 'gif'],
    maxSizeMB: 5,
  },
  banner: {
    mimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
    extensions: ['jpg', 'jpeg', 'png', 'webp'],
    maxSizeMB: 10,
  },
  portfolio: {
    mimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'],
    extensions: ['jpg', 'jpeg', 'png', 'webp', 'gif', 'pdf'],
    maxSizeMB: 15,
  },
  resume: {
    mimeTypes: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    extensions: ['pdf', 'doc', 'docx'],
    maxSizeMB: 10,
  },
  attachment: {
    mimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'application/pdf', 'text/plain'],
    extensions: ['jpg', 'jpeg', 'png', 'webp', 'pdf', 'txt'],
    maxSizeMB: 15,
  },
};

export function getCategoryConfig(category: StorageFileCategory) {
  return CATEGORY_CONFIG[category];
}

export function validateFile(
  file: File,
  category: StorageFileCategory,
  overrides?: {
    maxFileSizeMB?: number;
    allowedMimeTypes?: string[];
    allowedExtensions?: string[];
  },
): ValidationResult {
  const config = CATEGORY_CONFIG[category];
  const maxMB = overrides?.maxFileSizeMB ?? config.maxSizeMB;
  const allowedMimes = overrides?.allowedMimeTypes ?? config.mimeTypes;
  const allowedExts = overrides?.allowedExtensions ?? config.extensions;

  const sizeMB = file.size / (1024 * 1024);
  if (sizeMB > maxMB) {
    return { valid: false, error: new FileTooLargeError(sizeMB, maxMB).message };
  }

  if (!allowedMimes.includes(file.type)) {
    return { valid: false, error: new InvalidMimeTypeError(file.type, allowedMimes).message };
  }

  const ext = getExtension(file.name);
  if (ext && !allowedExts.includes(ext.toLowerCase())) {
    return { valid: false, error: new InvalidExtensionError(ext, allowedExts).message };
  }

  const sanitized = sanitizeFilename(file.name);
  if (!sanitized) {
    return { valid: false, error: new FileValidationError('Filename is empty after sanitization.').message };
  }

  return { valid: true, sanitizedFilename: sanitized };
}

export function sanitizeFilename(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  const base = lastDot > 0 ? filename.slice(0, lastDot) : filename;
  const ext = lastDot > 0 ? filename.slice(lastDot + 1) : '';

  const cleaned = base
    .normalize('NFKD')
    .replace(/[^\w\s.-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[.-]+|[.-]+$/g, '')
    .toLowerCase()
    .slice(0, 80);

  if (!cleaned) return '';
  return ext ? `${cleaned}.${ext.toLowerCase()}` : cleaned;
}

export function getExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot > 0 ? filename.slice(dot + 1) : '';
}

export function stripExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot > 0 ? filename.slice(0, dot) : filename;
}

export function isImage(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

export function isPdf(mimeType: string): boolean {
  return mimeType === 'application/pdf';
}

export function getMaxFileSizeMB(category: StorageFileCategory): number {
  return CATEGORY_CONFIG[category].maxSizeMB;
}

export function getAllowedMimeTypes(category: StorageFileCategory): string[] {
  return [...CATEGORY_CONFIG[category].mimeTypes];
}

export function getAllowedExtensions(category: StorageFileCategory): string[] {
  return [...CATEGORY_CONFIG[category].extensions];
}

export { DEFAULT_MAX_SIZE_MB };
